/**
 * TOTP enrollment page — first-time MFA setup.
 *
 * Spec 0005 §5.5: enroll → render QR + secret + 10 recovery codes →
 * acknowledge → confirm 6-digit code. Recovery codes shown ONCE; no
 * localStorage, no re-render. Behavior preserved across this refresh;
 * only the presentation changed.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, ShieldCheck } from "lucide-react";
import {
  asApiError,
  postTotpConfirm,
  postTotpEnroll,
  type TotpEnrollResponse,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

export function TOTPEnrollPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [enrollment, setEnrollment] = useState<TotpEnrollResponse | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // StrictMode-safe enrollment kickoff (see prior implementation for
  // rationale — the backend enroll is one-shot per AdminUser and the
  // dev double-invoke would otherwise 409).
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    if (!startedRef.current) {
      startedRef.current = true;
      void (async () => {
        try {
          const r = await postTotpEnroll();
          if (aliveRef.current) setEnrollment(r);
        } catch (err) {
          if (!aliveRef.current) return;
          const body = asApiError(err);
          if (body?.error === "totp_already_enrolled") {
            navigate("/totp/verify", { replace: true });
            return;
          }
          setEnrollError(
            body
              ? `Couldn't start enrollment (${body.error}).`
              : "Couldn't start enrollment. Check your network and reload.",
          );
        }
      })();
    }
    return () => {
      aliveRef.current = false;
    };
  }, [navigate]);

  async function copyCodes() {
    if (!enrollment) return;
    try {
      await navigator.clipboard.writeText(enrollment.recovery_codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  async function onConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setConfirmError(null);
    setSubmitting(true);
    try {
      await postTotpConfirm(code.trim());
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      const body = asApiError(err);
      if (body?.error === "totp_already_confirmed") {
        // Enrollment was confirmed already (e.g. a duplicate submit);
        // from here on the authenticator is only ever verified.
        navigate("/totp/verify", { replace: true });
        return;
      }
      if (body?.error === "totp_confirm_attempts_exceeded") {
        setConfirmError(
          "Too many incorrect codes. Restart enrollment and try again.",
        );
      } else if (body?.error === "invalid_code") {
        setConfirmError("That code didn't match. Try the next one.");
      } else if (body) {
        setConfirmError(`Couldn't confirm code (${body.error}).`);
      } else {
        setConfirmError("Couldn't confirm code. Check your network.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Set up two-factor authentication"
      description="Scan the QR with an authenticator app, then enter the 6-digit code to finish."
      wide
    >
      {enrollError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {enrollError}
        </div>
      ) : null}

      {!enrollment && !enrollError ? (
        <div className="space-y-4">
          <Skeleton className="mx-auto h-48 w-48" />
          <Skeleton className="mx-auto h-4 w-32" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : enrollment ? (
        <div className="space-y-6">
          <section
            aria-label="Authenticator setup"
            className="flex flex-col items-center gap-3 rounded-md border bg-muted/30 p-4"
          >
            <img
              src={`data:image/png;base64,${enrollment.qr_png_base64}`}
              alt="TOTP QR code"
              className="h-48 w-48 rounded-md border bg-white p-2"
            />
            <p className="text-xs text-muted-foreground">Or enter this secret manually:</p>
            <code
              data-testid="totp-secret"
              className="select-all rounded bg-background px-3 py-1.5 font-mono text-sm tracking-wider"
            >
              {enrollment.secret_b32}
            </code>
          </section>

          <section
            aria-label="Recovery codes"
            className="space-y-3 rounded-md border border-warning/30 bg-warning/5 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold">Recovery codes</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Store these somewhere safe — they're shown once and replace your
                  authenticator if you lose it.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyCodes}
              >
                {copied ? <Check /> : <Copy />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <ol
              data-testid="recovery-codes"
              className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm"
            >
              {enrollment.recovery_codes.map((c, i) => (
                <li key={c} className="select-all">
                  <span className="mr-2 text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  {c}
                </li>
              ))}
            </ol>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                aria-label="I've stored these codes somewhere safe"
                className="h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              I've stored these codes somewhere safe.
            </label>
          </section>

          <form className="space-y-3" onSubmit={onConfirm} noValidate>
            <div className="space-y-2">
              <Label htmlFor="totp">Authenticator code</Label>
              <Input
                id="totp"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={submitting || !acknowledged}
                placeholder="123456"
                className="text-center font-mono text-2xl tracking-[0.5em]"
              />
            </div>
            {confirmError ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
              >
                {confirmError}
              </div>
            ) : null}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !acknowledged}
              size="lg"
            >
              <ShieldCheck />
              {submitting ? "Confirming…" : "Continue"}
            </Button>
          </form>
        </div>
      ) : null}
    </AuthShell>
  );
}

export default TOTPEnrollPage;
